import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import values from 'ramda/src/values';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import CompanyActions from '~/actions/company';
import VendorActions from '~/actions/vendor';
import { BLOG_MOBILE_TAB } from '~/services/constants';
import CategoryMGT from './CategoryMGT';
import Drafts from './Drafts';
import Published from './Published';

const BlogAdmin = ({ blog, user, onBlogDetail }) => {
    const [active, setActive] = useState('drafts');
    const [searchTerms, setSearchTerms] = useState(null);
    const navigate = useNavigate();
    const loginUser = useSelector((state) => state.app.user?.community_user);
    const departments = useSelector((state) => state.company.blogCategories);
    const authors = blog?.admins || [];
    const categories = blog?.blog_categories ? values(blog.blog_categories) : [];

    const dispatch = useDispatch();

    useEffect(() => {
        const handleGetCategories = () => {
            dispatch(CompanyActions.getblogcategoriesRequest());
        };

        handleGetCategories();
        dispatch(CommunityActions.getcommunityuserRequest({ userId: user.id, blogId: blog?.blog?.id }, null, null, null, false));
    }, [blog?.blog?.id, dispatch, user.id]);

    const handleDeletePost = (post) => {
        const payload = {
            data: { post: { id: post.id, blog_id: post.data?.blog_id, user_id: post.user_id } },
            after: {
                type: 'GETCOMMUNITYBLOG_REQUEST',
                payload: { userId: post.user_id, blogId: post.data?.blog_id },
            },
        };
        dispatch(VendorActions.deleteblogpostRequest(payload));
    };

    const handleSelectOption = (option, data) => {
        switch (option) {
            case 'edit':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Edit Blog Post',
                        data: {
                            before: { post: data, userId: user.id, blog: { ...blog, id: blog?.blog?.id } },
                        },
                    })
                );
                break;

            case 'add':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Edit Blog Post',
                        data: {
                            before: { post: null, userId: user.id, blog: { ...blog, id: blog?.blog?.id } },
                        },
                        callBack: () => {
                            dispatch(CommunityActions.getcommunityuserRequest({ userId: blog?.user_id, blogId: blog?.blog?.id }, null, null, null, false));
                        },
                    })
                );
                break;

            case 'delete':
                dispatch(
                    AppActions.modalRequest({
                        type: 'Confirm',
                        data: {
                            before: { title: 'Delete', body: 'Are you sure you want to delete this post?' },
                        },
                        callBack: { onYes: () => handleDeletePost(data) },
                    })
                );
                break;

            default:
                break;
        }
    };

    const handleSearch = (e) => {
        const author = e.author ? find(propEq('id', e.author[0]), authors) : null;
        const department = e.categoryId ? find(propEq('id', e.categoryId[0]), departments) : null;
        const category = e.blogCategory ? e.blogCategory[0] : null;
        setSearchTerms({ search: e.search, author, department, category });
        dispatch(
            CommunityActions.getblogdetailsRequest(blog.user_id, {
                writerId: [e?.author],
                blogId: blog.blog?.id,
                categoryId: e?.categoryId,
                blogCategories: e?.blogCategory,
                search: e?.search,
            })
        );
    };

    const handleDetail = (e) => () => {
        const payload = {
            data: {
                community_user_id: loginUser?.id,
                events: [{ content_id: e.id, source: 'vr-component', type: 'click' }],
            },
        };
        dispatch(CommunityActions.addcontenteventRequest(payload));
        onBlogDetail(e);
        navigate(`/${e.author.entity.slug}/blog`);
    };

    return (
        <ErrorBoundary className="individual-blog-admin">
            <div className="blog-admin-header">
                <p className="dsl-b22 bold my-2">Blog Admin</p>
            </div>
            <ResponsiveTabs className="blog-admin-content" active={active} data={BLOG_MOBILE_TAB} onTab={(e) => setActive(e.id)} />
            <Tab eventKey="drafts" active={active}>
                <Drafts
                    data={blog?.drafts}
                    editable={blog?.can_edit_blog}
                    writers={blog?.admins}
                    authors={authors}
                    categories={categories}
                    departments={departments}
                    search={searchTerms}
                    onSelect={handleSelectOption}
                    onSearch={handleSearch}
                    onDetail={(e) => handleDetail(e)}
                />
            </Tab>
            <Tab eventKey="published" active={active}>
                <Published
                    data={blog?.posts}
                    userId={blog?.user_id}
                    blog={blog?.blog}
                    editable={blog?.can_edit_blog}
                    writers={blog?.admins}
                    authors={authors}
                    categories={categories}
                    departments={departments}
                    search={searchTerms}
                    onSelect={handleSelectOption}
                    onSearch={handleSearch}
                    onDetail={(e) => handleDetail(e)}
                />
            </Tab>
            <Tab eventKey="category" active={active}>
                <CategoryMGT blog={blog} companyId={blog.id} userId={user.id} />
            </Tab>
        </ErrorBoundary>
    );
};

BlogAdmin.propTypes = {
    blog: PropTypes.shape({
        user_id: PropTypes.number,
        admins: PropTypes.array,
        blog: PropTypes.shape({ id: PropTypes.number }),
        can_edit_blog: PropTypes.bool,
        drafts: PropTypes.shape({ data: PropTypes.array }),
        entity: PropTypes.shape({ id: PropTypes.number }),
        has_blog: PropTypes.bool,
        posts: PropTypes.shape({ data: PropTypes.array }),
        title: PropTypes.string,
    }),
    user: PropTypes.shape({ id: PropTypes.number }),
};

BlogAdmin.defaultProps = {
    blog: {
        user_id: 0,
        admins: [],
        blog: { id: 0 },
        can_edit_blog: false,
        drafts: { data: [] },
        entity: { id: 0 },
        has_blog: false,
        posts: { data: [] },
        title: '',
    },
    user: { id: 0 },
};

export default BlogAdmin;
